// Short-lived file-access tokens for inline media.
//
// WHY THIS EXISTS
// ---------------
// `<img>`, `<audio>` and `<iframe>` cannot send an Authorization header, so
// every inline attachment in the app used to authenticate with the `aone_sso`
// cookie alone — the ONLY surface that did. Everything else (axios calls, the
// download/copy fetches in pages/arey/shared.js) rides the durable localStorage
// Bearer, which axios refreshes indefinitely. That split produced three
// separate incidents where chat worked perfectly while every image 401'd:
//   2026-07-21  cookie lapsed → downloads 401 ("file store unreachable")
//   2026-07-24  cookie JWT (8h) died under a live Bearer → all-employee
//               broken thumbnails; protect() now re-issues the cookie
//   2026-07-25  a user on https://aone-erp-production.up.railway.app — the
//               Set-Cookie carries the app's cookie domain, which the browser silently
//               discards on that host, so the cookie can NEVER be set and the
//               2026-07-24 self-heal can never take effect
// Each fix kept the cookie alive from a new angle. This removes the dependency
// instead: the backend already mints a 5-minute `purpose:'file-access'` JWT at
// GET /api/files/token, and the /api/files/*key proxy already accepts it as
// `?ft=` (backend/src/app.js). Nothing in the frontend had ever called it.
//
// The token is deliberately narrow — purpose-scoped, 5 minutes, useless for API
// access (protect() rejects any token carrying a `purpose`) — which is what
// makes it safe to put in a URL where it can leak via logs and referrers.
import { useEffect, useState } from 'react';
import api from './api';

// Server mints 5m; treat as stale at 4m so an in-flight image never races the
// expiry. Holding it stable for the full window also keeps `src` strings
// identical across re-renders, so React doesn't thrash the image cache.
const TTL_MS = 4 * 60 * 1000;

let token = null;
let fetchedAt = 0;
let inflight = null;
const subscribers = new Set();

/** Current token, or null when absent/stale. Never triggers a fetch. */
export function getFileToken() {
  return token && Date.now() - fetchedAt < TTL_MS ? token : null;
}

/**
 * Resolves to a fresh token, coalescing concurrent callers onto one request —
 * a channel with 40 images mounts 40 consumers but issues a single GET (the
 * backend rate-limits this endpoint to 30/user/min).
 * Never rejects: on failure we fall back to cookie-only behaviour.
 */
export function ensureFileToken() {
  const cached = getFileToken();
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;
  inflight = api.get('/files/token')
    .then((res) => {
      token = res?.data?.token || null;
      fetchedAt = Date.now();
      subscribers.forEach((fn) => { try { fn(token); } catch { /* consumer unmounted */ } });
      return token;
    })
    .catch(() => null)
    .finally(() => { inflight = null; });
  return inflight;
}

/**
 * Appends `?ft=` to our own proxy URLs. Non-proxy URLs (S3 direct, data: URIs,
 * external images) pass through untouched. With no token yet, the bare URL is
 * returned so the cookie still gets its chance — the token only ever adds a
 * credential, it never removes one.
 */
export function fileSrc(url, tok = getFileToken()) {
  if (!url || typeof url !== 'string' || !url.startsWith('/api/files/')) return url;
  if (!tok) return url;
  return `${url}${url.includes('?') ? '&' : '?'}ft=${encodeURIComponent(tok)}`;
}

/**
 * Subscribes a component to the shared token. Mounting is what drives refresh:
 * a message arriving an hour later mounts a new consumer, which re-fetches if
 * the cached token has gone stale. No timers, so nothing keeps polling in a
 * background tab.
 */
export function useFileToken() {
  const [tok, setTok] = useState(getFileToken);
  useEffect(() => {
    subscribers.add(setTok);
    ensureFileToken().then((t) => setTok(t));
    return () => { subscribers.delete(setTok); };
  }, []);
  return tok;
}
